import React, { memo, useMemo, useState, useCallback, useEffect, createContext } from 'react';
import { Box, Flex, Heading, Stack, Divider, Text } from '@chakra-ui/layout';
import { useBoolean } from '@chakra-ui/react';
import { AuthorizeView, Button, Label, Loading, Modal } from 'components';
import { RouteComponentProps } from 'react-router';
import * as Styles from './styles';
import { AdditionalForm, FooterTabs } from './components';
import { checkPermission } from 'utils/authentication';
import { IDemandServiceType, IClientDemand, IContent } from 'types/demands';
import { getClientDemandData, getDemandData } from 'services/getDemandData';
import { getAnexoComment } from 'services/commentsClientDemand';
import { format, parse } from 'date-fns';
import { assessmentTypesOptions, internalAreaOptions } from 'constants/demand';
import { formatCurrency } from 'utils/number';
import { extname } from 'utils/files';
import { demandStatusValues } from 'constants/demands';
import { getIDemandServiceTypes } from 'services/demands';
import { colors } from 'theme/colors';
import { getParticipants } from 'services/participants';
import { getLoggedUser } from 'utils/storage';
import useEventBus from 'hooks/useEventBus';
import { changeStatusDemand } from 'services/changeStatusDemand';
import { openNotification } from 'utils/notification';
import { getUserData } from 'services/getUserData';

interface IProps extends RouteComponentProps<{ id: string }> {}
export const DemandCtx = createContext<IClientDemand>({} as IClientDemand);
const ClientDemandDetailComponent: React.FC<IProps> = ({ history, match }) => {
  const eventBus = useEventBus();
  useEffect(() => {
    eventBus.dispatchEvent('onChangeTitle', 'Detalhes da solicitação');
  }, []);
  const [disable, setDisable] = useBoolean();
  const [hide, setHide] = useBoolean();
  const demandID = useMemo(() => parseInt(match.params.id), [match.params]);
  const [demand, setDemand] = useState<IClientDemand>();
  const [documents, setDocuments] = useState<IContent[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [serviceTypes, setServiceTypes] = useState<IDemandServiceType[]>([]);
  const [formData, setFormData] = useState<IClientDemand>({} as IClientDemand);
  const [btnStatus, setBtnStatus] = useState<TBtnStat>(
    checkPermission({ onlyClient: true }) ? 'Solicitar Cancelamento' : 'Cancelar Demanda',
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isC, setIsC] = useState(true);
  const [mod, setMod] = useState('');

  const [participants, setParticipants] = useState<{ name: string; email?: string }[]>([]);
  const [userLogged, setUserLogged] = useState<string>('');
  const [initialForm, setInitialForm] = useState<{
    areaInterna?: {
      id: string;
      name: string;
    };
    lawyer?: {
      id: number;
      name: string;
    };
    valor: string;
    horas?: string;
  }>({
    areaInterna: undefined,
    lawyer: undefined,
    horas: '',
    valor: '',
  });
  type TBtnStat =
    | 'Solicitar Cancelamento'
    | 'Cancelamento Solicitado'
    | 'Cancelar Demanda'
    | 'Cancelado';
  const handleConfirm = useCallback(async () => {
    try {
      if (checkPermission({ onlyAdmin: true })) {
        setIsLoading(true);
        openNotification('Cancelamento confirmado com sucesso.', 'success');
        changeStatusDemand(+demandID, demandStatusValues.canceled)
          .then(() => setDisable.on())
          .then(() => setBtnStatus('Cancelado'))
          .then(() => {
            setIsLoading(false);
            window.location.reload();
          });
      } else {
        setIsLoading(true);
        changeStatusDemand(+demandID, demandStatusValues.toCancel)
          .then(() => setDisable.on())
          .then(() => setBtnStatus('Cancelamento Solicitado'))
          .then(() => {
            setIsLoading(false);
            openNotification('Cancelamento solicitado com sucesso.', 'success');
          });
      }
    } catch (e) {}
  }, [demandID]);

  interface IProps {
    title: TBtnStat;
    header: string;
    onClick(): void;
    disabled: boolean;
    hide: boolean;
  }
  const CancelModal = useCallback(
    (props: IProps) => {
      if (!props.hide) {
        return (
          <Modal
            title={props.title}
            header={props.header}
            body="Tem certeza que deseja cancelar esta demanda? O time Daniel não dará continuidade a esta solicitação."
            yesText="Sim, quero cancelar"
            noText="Não"
            onClick={handleConfirm}
            disabled={props.disabled}
          />
        );
      }
    },
    [hide],
  );

  const handleGetServiceTypes = useCallback(async () => {
    try {
      const response = await getIDemandServiceTypes();

      setServiceTypes(response.data);
    } catch (error) {}
  }, []);

  const idLogged = getLoggedUser().id;

  const handleGetData = useCallback(async () => {
    const response = await getUserData(String(idLogged));

    setUserLogged(String(response.data.employeeDanielLaw));

    if (!demandID) return;

    try {
      setIsLoading(true);
      let data: IClientDemand | null = null;
      setHide.on();
      const response = await getClientDemandData(demandID);
      if (response.data.status !== demandStatusValues.pending) {
        setHide.on();
      } else {
        setHide.off();
      }
      if (response.data.status === demandStatusValues.toCancel) {
        setHide.off();
        setDisable.on();
        setBtnStatus('Cancelamento Solicitado');
      }

      data = {
        ...response.data,
        solicitacaoDocumentos: response.data.solicitacaoDocumentos
          ? response.data.solicitacaoDocumentos.map(i => ({ ...i, ext: extname(i.filename) }))
          : [],
        prazo: format(new Date(response.data.prazo), 'dd/MM/yyyy'),
        serviceType: response.data.serviceType.id,
        status: response.data.status || '',
        consumoHoras: !!response.data.consumoHoras,
      };
      setFormData({ ...data, demandID: demandID });
      setMod(response.data.serviceType.name);
      if (checkPermission({ onlyAdmin: true })) {
        const res = await getDemandData(demandID);
        if (res.data.status !== demandStatusValues.pending) setHide.on();
        if (res.data.status === demandStatusValues.toCancel) {
          setDisable.on();
          setBtnStatus('Cancelamento Solicitado');
        }
        data = {
          ...data,
          autorDemanda: res.data.autorDemanda,
          areaDemandante: res.data.areaDemandante,
          createdAt: res.data.createdAt
            ? format(parse(res.data.createdAt, 'yyyy-MM-dd', Date.now()), 'dd/MM/yyyy')
            : '',
        };
        setFormData({ ...data, demandID: demandID });
        setInitialForm({
          valor: formatCurrency(res.data.valor),
          horas: (res.data.horas || '').toString().replace('.', ','),
          areaInterna: internalAreaOptions.find(
            i => i.id === res.data.areaInterna || i.name === res.data.areaInterna,
          ),
          // @ts-ignore
          lawyer: res.data.lawyer,
        });
      }
      if (!data.createdBy) history.replace(`/demands/edit/${demandID}`);
      const { data: dt } = await getParticipants(demandID);
      const resp = await getAnexoComment(demandID);
      setParticipants(dt.map(i => ({ name: i.user.name, email: i.user.email })));
      setDemand(data);

      const docs = data.solicitacaoDocumentos ? data.solicitacaoDocumentos : [];
      setDocuments([...docs, ...resp.data.content]);

      setIsC(parseInt(data.serviceType + '') === 2);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [demandID]);

  useEffect(() => {
    handleGetData();
    handleGetServiceTypes();
  }, [handleGetData, handleGetServiceTypes, demand?.status]);

  const avalType = useMemo(() => {
    return (
      [...assessmentTypesOptions, ...internalAreaOptions].find(
        i => i.id === demand?.tipoConsulta || i.id === demand?.tipoAvaliacao,
      )?.name || ''
    );
  }, [demand]);

  const modality = useMemo(() => {
    return serviceTypes.find(i => i.id === demand?.serviceType)?.name || mod;
  }, [demand?.serviceType]);

  return (
    <Flex w="100%" align="center" direction={'column'}>
      {checkPermission({ onlyAdmin: true }) && demand?.status === 'CANCELAMENTO' && (
        <Flex
          width="75%"
          padding="2rem"
          background="#fff"
          borderRadius="10px"
          boxShadow={'0px 0px 15px rgba(0, 0, 0, 0.25);'}
          marginBottom="1rem"
        >
          <Box>
            <Heading as="h3" color="red" fontSize="1.1rem" marginBottom="1rem">
              Solicitação de cancelamento
            </Heading>

            <Text>
              Cliente realizou a solicitação do cancelamento da solicitação. Ao aceitar o
              cancelamento a mesma será despriorizada e o cliente receberá a sua resposta.
            </Text>
          </Box>

          <Flex justifyContent="flex-end" gridGap="1rem" align="flex-end">
            <Button
              variant="accept"
              onClick={() => {
                openNotification('Solicitação de cancelamento recusada.', 'success');

                changeStatusDemand(+demandID, demandStatusValues.pending)
                  .then(() => setDisable.on())
                  .then(() => setBtnStatus('Solicitar Cancelamento'));

                setTimeout(() => window.location.reload(), 2000);
              }}
            >
              Recusar
            </Button>
            <Button variant="danger" onClick={() => handleConfirm()}>
              Aceitar
            </Button>
          </Flex>
        </Flex>
      )}
      <Box
        width="75%"
        padding="2rem"
        background="#fff"
        borderRadius="10px"
        boxShadow={'0px 0px 15px rgba(0, 0, 0, 0.25);'}
      >
        <Stack direction={['column', 'row']} spacing="1rem" mb="1rem" w="100%">
          {demand?.createdBy?.employeeDanielLaw === 'DANIEL_LAW' ? (
            <Box>
              <Heading size="lg" as="h3" color="brand.primary">
                Criado por Administrador Daniel Law
              </Heading>
            </Box>
          ) : (
            <Box>
              <Heading size="lg" as="h3" color="brand.primary">
                {demand?.createdBy?.client.name}
              </Heading>
              {demand?.createdBy?.client.id}
            </Box>
          )}
          {/* quando logado com cliente e vizualizar uma solicitação de um adm ou clietn adm nao posso ver os botoes de editar e solicitar cancelamento */}
          {userLogged === 'CLIENTE_DANIEL_LAW' &&
          (demand?.createdBy.employeeDanielLaw === 'DANIEL_LAW' ||
            demand?.createdBy.employeeDanielLaw === 'CLIENTE_ADMINISTRADOR') ? (
            <></>
          ) : (
            <Flex flex="1" justifyContent="flex-end">
              {CancelModal({
                title: btnStatus,
                header: 'Cancelar Demanda?',
                onClick: handleConfirm,
                disabled: disable,
                hide: hide,
              })}

              {demand?.status === demandStatusValues.pending && !getLoggedUser().isParticipant && (
                <Button
                  variant="primary"
                  href={`/clientDemands/edit/${demandID}`}
                  ml={1}
                  leftIcon="edit"
                  outline
                >
                  Editar demanda
                </Button>
              )}
            </Flex>
          )}
        </Stack>
        <Divider />
        {!isLoading ? (
          <Box background="#fff" borderRadius="10px" paddingTop="2rem">
            <Styles.Body>
              <h2>{demand?.demanda}</h2>

              <div>
                <i>{demand?.descricaoTrabalho}</i>
              </div>

              <Flex mt="2rem" direction="column" gridGap="2rem">
                <AuthorizeView onlyAdmin>
                  <Flex direction="column">
                    <Label color="#5a646e">
                      A solicitação é válida para consumo do banco de horas ?
                    </Label>

                    {demand?.consumoHoras ? (
                      <Label color="#22272D">SIM</Label>
                    ) : (
                      <Label color="#22272D">NÃO</Label>
                    )}
                  </Flex>
                </AuthorizeView>

                <Flex flexWrap="wrap" gridGap="2rem">
                  <AuthorizeView onlyAdmin>
                    <>
                      <Flex direction="column" flexBasis="30%" gridGap="0px">
                        <Label color="#5a646e">Autor da demanda</Label>

                        <Styles.TextPagraph>{demand?.createdBy.name}</Styles.TextPagraph>
                      </Flex>

                      <Flex direction="column" flexBasis="30%" gridGap="0px">
                        <Label color="#5a646e">Área demandante</Label>

                        <Styles.TextPagraph>{demand?.areaDemandante}</Styles.TextPagraph>
                      </Flex>

                      <Flex direction="column" flexBasis="30%" gridGap="0px">
                        <Label color="#5a646e">Data da solicitação</Label>

                        <Styles.TextPagraph>{demand?.createdAt}</Styles.TextPagraph>
                      </Flex>
                    </>
                  </AuthorizeView>

                  <Flex direction="column" flexBasis="30%" gridGap="0px">
                    <Label color="#5a646e">Modalidade</Label>

                    <Styles.TextPagraph>{modality}</Styles.TextPagraph>
                  </Flex>

                  <Flex direction="column" flexBasis="30%" gridGap="0px">
                    <Label color="#5a646e">
                      Tipo de {parseInt(demand?.serviceType + '') !== 2 ? 'avaliação' : 'consulta'}
                    </Label>

                    <Styles.TextPagraph>{avalType}</Styles.TextPagraph>
                  </Flex>

                  <Flex direction="column" flexBasis="30%" gridGap="0px">
                    <Label color="#5a646e">Prazo</Label>

                    <Styles.TextPagraph>
                      <span id="red">
                        {demand?.prazoAtendimento.match(/sla/gi) ? '' : 'Urgente - '}
                      </span>
                      {demand?.prazo}
                    </Styles.TextPagraph>
                  </Flex>
                  <AuthorizeView onlyClient>
                    <Flex direction="column">
                      <Label>A solicitação é válida para consumo do banco de horas ?</Label>

                      {demand?.consumoHoras ? 'Sim' : 'Não'}
                    </Flex>
                  </AuthorizeView>
                </Flex>

                <Flex direction="column" gridGap="6px">
                  <Heading as="h4" fontSize="13px">
                    Participantes
                  </Heading>

                  {participants.map((i, k) => (
                    <Flex
                      key={k}
                      flexDirection="row"
                      alignItems="center"
                      pl="16px"
                      pr="22px"
                      width="100%"
                      mt="6px"
                      minHeight="44px"
                      py="8px"
                      bg={'brand.white'}
                      border="solid 1px #000"
                      borderRadius="4px"
                    >
                      <Flex width="50%">
                        <Heading
                          fontWeight="700"
                          as="span"
                          fontSize="14px"
                          color={colors.brand.black700}
                        >
                          {i?.email}
                        </Heading>
                      </Flex>
                      <Flex width="50%">
                        <Heading
                          as="span"
                          fontWeight="700"
                          fontSize="14px"
                          color={colors.brand.black700}
                        >
                          {i.name}
                        </Heading>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>

                <AdditionalForm data={demand} initialData={initialForm} id={demandID}>
                  <DemandCtx.Provider value={formData}>
                    <FooterTabs
                      updateFiles={setDocuments}
                      date={demand?.createdAt}
                      clientName={
                        checkPermission({ onlyAdmin: true }) ? demand?.createdBy.client.name : ''
                      }
                      docs={demand?.solicitacaoDocumentos}
                      files={documents}
                    />
                  </DemandCtx.Provider>
                </AdditionalForm>
              </Flex>
            </Styles.Body>
          </Box>
        ) : (
          <Loading />
        )}
      </Box>
    </Flex>
  );
};

export const ClientDemandDetail = memo(ClientDemandDetailComponent);
